"use client";

import React from "react";
import { useIsMobile } from "../hooks/use-mobile";
import { ChevronDown, ChevronUp } from "lucide-react";
import MainLayout from "../components/MainLayout";

const PrivacyPolicy = () => {
  const isMobile = useIsMobile();
  const [expandedSections, setExpandedSections] = React.useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const isSectionExpanded = (sectionId) => {
    return expandedSections[sectionId] !== false; // Default to expanded
  };

  const renderSection = (id, title, content, level = 2) => {
    const isExpanded = isSectionExpanded(id);
    const HeadingTag = `h${level}`;

    return (
      <div id={id} className="mb-8">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection(id)}
        >
          <HeadingTag
            className={`font-bold ${
              level === 1 ? "text-2xl" : level === 2 ? "text-xl" : "text-lg"
            } mb-2`}
          >
            {title}
          </HeadingTag>
          {isMobile && (
            <button className="p-1 text-gray-500">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          )}
        </div>
        {(!isMobile || isExpanded) && (
          <div className="text-gray-600">{content}</div>
        )}
      </div>
    );
  };

  return (
    <MainLayout rs={false}>
      <div className=" pl-2 max-w-4xl mx-auto font-afacad">
        <div className="mt-4 mb-10">
          <h1 className="text-3xl font-bold mb-2 font-fraunces">
            PRIVACY POLICY
          </h1>
          <p className="text-gray-500">Last updated April 01, 2025</p>
        </div>

        <div className="mb-8">
          <p className="text-gray-600">
            This Privacy Notice for <strong>Lean Tech Ops LLC</strong> ("we,"
            "us," or "our"), describes how and why we might access, collect,
            store, use, and/or share ("process") your personal information when
            you use our services ("Services"), including when you:
          </p>
          <ul className="list-disc pl-8 mt-4 text-gray-600">
            <li>
              Visit our website at{" "}
              <a
                href="https://gallyo.com"
                className="text-blue-600 hover:underline"
              >
                gallyo.com
              </a>
              , or any website of ours that links to this Privacy Notice
            </li>
            <li>
              Download and use our mobile application (gallyo), or any other
              application of ours that links to this Privacy Notice
            </li>
            <li>
              Use gallyo. gallyo is a community-centric platform that connects
              the South Asian diaspora with local businesses, services, events,
              and experiences tailored to their unique cultural needs. It
              provides personalized, location-based content recommendations and
              event planning services, enabling users to discover, endorse, or
              pass on experiences seamlessly.
            </li>
            <li>
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p className="mt-4 text-gray-600">
            <strong>Questions or concerns?</strong> Reading this Privacy Notice
            will help you understand your privacy rights and choices. We are
            responsible for making decisions about how your personal information
            is processed. If you do not agree with our policies and practices,
            please do not use our Services. If you still have any questions or
            concerns, please contact us at{" "}
            <a
              href="mailto:connect@leantechops.co"
              className="text-blue-600 hover:underline"
            >
              connect@leantechops.co
            </a>
            .
          </p>
        </div>

        {renderSection(
          "summary",
          "SUMMARY OF KEY POINTS",
          <div>
            <p className="mb-4">
              <strong>
                <em>
                  This summary provides key points from our Privacy Notice, but
                  you can find out more details about any of these topics by
                  clicking the link following each key point or by using our
                  table of contents below to find the section you are looking
                  for.
                </em>
              </strong>
            </p>

            <p className="mb-2">
              <strong>What personal information do we process?</strong> When you
              visit, use, or navigate our Services, we may process personal
              information depending on how you interact with us and the
              Services, the choices you make, and the products and features you
              use.
            </p>

            <p className="mb-2">
              <strong>Do we process any sensitive personal information?</strong>{" "}
              Some of the information may be considered "special" or "sensitive"
              in certain jurisdictions, for example your racial or ethnic
              origins, sexual orientation, and religious beliefs. We do not
              process sensitive personal information.
            </p>

            <p className="mb-2">
              <strong>Do we collect any information from third parties?</strong>{" "}
              We do not collect any information from third parties.
            </p>

            <p className="mb-2">
              <strong>How do we process your information?</strong> We process
              your information to provide, improve, and administer our Services,
              communicate with you, for security and fraud prevention, and to
              comply with law. We may also process your information for other
              purposes with your consent. We process your information only when
              we have a valid legal reason to do so.
            </p>

            <p className="mb-2">
              <strong>
                In what situations and with which parties do we share personal
                information?
              </strong>{" "}
              We may share information in specific situations and with specific
              third parties.
            </p>

            <p className="mb-2">
              <strong>How do we keep your information safe?</strong> We have
              adequate organizational and technical processes and procedures in
              place to protect your personal information. However, no electronic
              transmission over the internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
            </p>

            <p className="mb-2">
              <strong>What are your rights?</strong> Depending on where you are
              located geographically, the applicable privacy law may mean you
              have certain rights regarding your personal information.
            </p>

            <p className="mb-2">
              <strong>How do you exercise your rights?</strong> The easiest way
              to exercise your rights is by submitting a data subject access
              request, or by contacting us. We will consider and act upon any
              request in accordance with applicable data protection laws.
            </p>
          </div>,
          1
        )}

        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">TABLE OF CONTENTS</h2>
          <ol className="list-decimal pl-8 text-blue-600">
            <li className="mb-2">
              <a href="#infocollect" className="hover:underline">
                WHAT INFORMATION DO WE COLLECT?
              </a>
            </li>
            <li className="mb-2">
              <a href="#infouse" className="hover:underline">
                HOW DO WE PROCESS YOUR INFORMATION?
              </a>
            </li>
            <li className="mb-2">
              <a href="#legalbases" className="hover:underline">
                WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
                INFORMATION?
              </a>
            </li>
            <li className="mb-2">
              <a href="#whoshare" className="hover:underline">
                WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
              </a>
            </li>
            <li className="mb-2">
              <a href="#3pwebsites" className="hover:underline">
                WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
              </a>
            </li>
            <li className="mb-2">
              <a href="#cookies" className="hover:underline">
                DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
              </a>
            </li>
            <li className="mb-2">
              <a href="#inforetain" className="hover:underline">
                HOW LONG DO WE KEEP YOUR INFORMATION?
              </a>
            </li>
            <li className="mb-2">
              <a href="#infosafe" className="hover:underline">
                HOW DO WE KEEP YOUR INFORMATION SAFE?
              </a>
            </li>
            <li className="mb-2">
              <a href="#infominors" className="hover:underline">
                DO WE COLLECT INFORMATION FROM MINORS?
              </a>
            </li>
            <li className="mb-2">
              <a href="#privacyrights" className="hover:underline">
                WHAT ARE YOUR PRIVACY RIGHTS?
              </a>
            </li>
            <li className="mb-2">
              <a href="#DNT" className="hover:underline">
                CONTROLS FOR DO-NOT-TRACK FEATURES
              </a>
            </li>
            <li className="mb-2">
              <a href="#uslaws" className="hover:underline">
                DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              </a>
            </li>
            <li className="mb-2">
              <a href="#policyupdates" className="hover:underline">
                DO WE MAKE UPDATES TO THIS NOTICE?
              </a>
            </li>
            <li className="mb-2">
              <a href="#contact" className="hover:underline">
                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              </a>
            </li>
            <li className="mb-2">
              <a href="#request" className="hover:underline">
                HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
                YOU?
              </a>
            </li>
          </ol>
        </div>

        {renderSection(
          "infocollect",
          "1. WHAT INFORMATION DO WE COLLECT?",
          <div>
            {renderSection(
              "personalinfo",
              "Personal information you disclose to us",
              <div>
                <p className="mb-4">
                  <strong>
                    <em>In Short:</em>
                  </strong>{" "}
                  <em>
                    We collect personal information that you provide to us.
                  </em>
                </p>
                <p className="mb-4">
                  We collect personal information that you voluntarily provide
                  to us when you register on the Services, express an interest
                  in obtaining information about us or our products and
                  Services, when you participate in activities on the Services,
                  or otherwise when you contact us.
                </p>
                <p className="mb-4">
                  <strong>Personal Information Provided by You.</strong> The
                  personal information that we collect depends on the context of
                  your interactions with us and the Services, the choices you
                  make, and the products and features you use. The personal
                  information we collect may include the following:
                </p>
                <ul className="list-disc pl-8 mb-4 text-gray-600">
                  <li>phone numbers</li>
                  <li>email addresses</li>
                  <li>usernames</li>
                  <li>contact preferences</li>
                </ul>
                <p className="mb-4">
                  <strong>Sensitive Information.</strong> We do not process
                  sensitive information.
                </p>
                <p className="mb-4">
                  <strong>Payment Data.</strong> We may collect data necessary
                  to process your payment if you choose to make purchases, such
                  as your payment instrument number, and the security code
                  associated with your payment instrument. All payment data is
                  handled and stored by __________. You may find their privacy
                  notice link(s) here:{" "}
                  <a href="#" className="text-blue-600 hover:underline">
                    __________
                  </a>
                  .
                </p>
                <p className="mb-4">
                  <strong>Application Data.</strong> If you use our
                  application(s), we also may collect the following information
                  if you choose to provide us with access or permission:
                </p>
                <ul className="list-disc pl-8 mb-4 text-gray-600">
                  <li>
                    <em>Geolocation Information.</em> We may request access or
                    permission to track location-based information from your
                    mobile device, either continuously or while you are using
                    our mobile application(s), to provide certain location-based
                    services. If you wish to change our access or permissions,
                    you may do so in your device's settings.
                  </li>
                  <li>
                    <em>Push Notifications.</em> We may request to send you push
                    notifications regarding your account or certain features of
                    the application(s). If you wish to opt out from receiving
                    these types of communications, you may turn them off in your
                    device's settings.
                  </li>
                </ul>
                <p className="mb-4">
                  This information is primarily needed to maintain the security
                  and operation of our application(s), for troubleshooting, and
                  for our internal analytics and reporting purposes.
                </p>
                <p className="mb-4">
                  All personal information that you provide to us must be true,
                  complete, and accurate, and you must notify us of any changes
                  to such personal information.
                </p>
              </div>,
              3
            )}

            {renderSection(
              "infocollect-auto",
              "Information automatically collected",
              <div>
                <p className="mb-4">
                  <strong>
                    <em>In Short:</em>
                  </strong>{" "}
                  <em>
                    Some information — such as your Internet Protocol (IP)
                    address and/or browser and device characteristics — is
                    collected automatically when you visit our Services.
                  </em>
                </p>
                <p className="mb-4">
                  We automatically collect certain information when you visit,
                  use, or navigate the Services. This information does not
                  reveal your specific identity (like your name or contact
                  information) but may include device and usage information,
                  such as your IP address, browser and device characteristics,
                  operating system, language preferences, referring URLs, device
                  name, country, location, information about how and when you
                  use our Services, and other technical information. This
                  information is primarily needed to maintain the security and
                  operation of our Services, and for our internal analytics and
                  reporting purposes.
                </p>
                <p className="mb-4">
                  Like many businesses, we also collect information through
                  cookies and similar technologies.
                </p>
                <p className="mb-4">The information we collect includes:</p>
                <ul className="list-disc pl-8 mb-4 text-gray-600">
                  <li>
                    <em>Log and Usage Data.</em> Log and usage data is
                    service-related, diagnostic, usage, and performance
                    information our servers automatically collect when you
                    access or use our Services and which we record in log files.
                    Depending on how you interact with us, this log data may
                    include your IP address, device information, browser type,
                    and settings and information about your activity in the
                    Services (such as the date/time stamps associated with your
                    usage, pages and files viewed, searches, and other actions
                    you take such as which features you use), device event
                    information (such as system activity, error reports
                    (sometimes called "crash dumps"), and hardware settings).
                  </li>
                  <li>
                    <em>Device Data.</em> We collect device data such as
                    information about your computer, phone, tablet, or other
                    device you use to access the Services. Depending on the
                    device used, this device data may include information such
                    as your IP address (or proxy server), device and application
                    identification numbers, location, browser type, hardware
                    model, Internet service provider and/or mobile carrier,
                    operating system, and system configuration information.
                  </li>
                  <li>
                    <em>Location Data.</em> We collect location data such as
                    information about your device's location, which can be
                    either precise or imprecise. How much information we collect
                    depends on the type and settings of the device you use to
                    access the Services. For example, we may use GPS and other
                    technologies to collect geolocation data that tells us your
                    current location (based on your IP address). You can opt out
                    of allowing us to collect this information either by
                    refusing access to the information or by disabling your
                    Location setting on your device. However, if you choose to
                    opt out, you may not be able to use certain aspects of the
                    Services.
                  </li>
                </ul>
              </div>,
              3
            )}

            {renderSection(
              "google-api",
              "Google API",
              <div>
                <p className="mb-4">
                  Our use of information received from Google APIs will adhere
                  to{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Limited Use requirements
                  </a>
                  .
                </p>
              </div>,
              3
            )}
          </div>
        )}

        {renderSection(
          "infouse",
          "2. HOW DO WE PROCESS YOUR INFORMATION?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                We process your information to provide, improve, and administer
                our Services, communicate with you, for security and fraud
                prevention, and to comply with law. We may also process your
                information for other purposes with your consent.
              </em>
            </p>
            <p className="mb-4">
              <strong>
                We process your personal information for a variety of reasons,
                depending on how you interact with our Services, including:
              </strong>
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>
                <strong>
                  To facilitate account creation and authentication and
                  otherwise manage user accounts.
                </strong>{" "}
                We may process your information so you can create and log in to
                your account, as well as keep your account in working order.
              </li>
              <li>
                <strong>
                  To deliver and facilitate delivery of services to the user.
                </strong>{" "}
                We may process your information to provide you with the
                requested service.
              </li>
              <li>
                <strong>
                  To respond to user inquiries/offer support to users.
                </strong>{" "}
                We may process your information to respond to your inquiries and
                solve any potential issues you might have with the requested
                service.
              </li>
              <li>
                <strong>To deliver targeted advertising to you.</strong> We may
                process your information to develop and display personalized
                content and advertising tailored to your interests, location,
                and more.
              </li>
              <li>
                <strong>To post testimonials.</strong> We post testimonials on
                our Services that may contain personal information.
              </li>
              <li>
                <strong>
                  To evaluate and improve our Services, products, marketing, and
                  your experience.
                </strong>{" "}
                We may process your information when we believe it is necessary
                to identify usage trends, determine the effectiveness of our
                promotional campaigns, and to evaluate and improve our Services,
                products, marketing, and your experience.
              </li>
              <li>
                <strong>To identify usage trends.</strong> We may process
                information about how you use our Services to better understand
                how they are being used so we can improve them.
              </li>
              <li>
                <strong>
                  To determine the effectiveness of our marketing and
                  promotional campaigns.
                </strong>{" "}
                We may process your information to better understand how to
                provide marketing and promotional campaigns that are most
                relevant to you.
              </li>
              <li>
                <strong>Usage Trends.</strong> We may use your information for
                other business purposes, such as data analysis, identifying
                usage trends, determining the effectiveness of our promotional
                campaigns and to evaluate and improve our Services, products,
                marketing and your experience.
              </li>
            </ul>
          </div>
        )}

        {renderSection(
          "legalbases",
          "3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                We only process your personal information when we believe it is
                necessary and we have a valid legal reason (i.e., legal basis)
                to do so under applicable law, like with your consent, to comply
                with laws, to provide you with services to enter into or fulfill
                our contractual obligations, to protect your rights, or to
                fulfill our legitimate business interests.
              </em>
            </p>
            <p className="mb-4">
              <strong>
                <u>
                  <em>
                    If you are located in Canada, this section applies to you.
                  </em>
                </u>
              </strong>
            </p>
            <p className="mb-4">
              We may process your information if you have given us specific
              permission (i.e., express consent) to use your personal
              information for a specific purpose, or in situations where your
              permission can be inferred (i.e., implied consent). You can{" "}
              <a
                href="#withdrawconsent"
                className="text-blue-600 hover:underline"
              >
                withdraw your consent
              </a>{" "}
              at any time.
            </p>
            <p className="mb-4">
              In some exceptional cases, we may be legally permitted under
              applicable law to process your information without your consent,
              including, for example:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>
                If collection is clearly in the interests of an individual and
                consent cannot be obtained in a timely way
              </li>
              <li>For investigations and fraud detection and prevention</li>
              <li>
                For business transactions provided certain conditions are met
              </li>
              <li>
                If it is contained in a witness statement and the collection is
                necessary to assess, process, or settle an insurance claim
              </li>
              <li>
                For identifying injured, ill, or deceased persons and
                communicating with next of kin
              </li>
              <li>
                If we have reasonable grounds to believe an individual has been,
                is, or may be victim of financial abuse
              </li>
              <li>
                If it is reasonable to expect collection and use with consent
                would compromise the availability or the accuracy of the
                information and the collection is reasonable for purposes
                related to investigating a breach of an agreement or a
                contravention of the laws of Canada or a province
              </li>
              <li>
                If disclosure is required to comply with a subpoena, warrant,
                court order, or rules of the court relating to the production of
                records
              </li>
              <li>
                If it was produced by an individual in the course of their
                employment, business, or profession and the collection is
                consistent with the purposes for which the information was
                produced
              </li>
              <li>
                If the collection is solely for journalistic, artistic, or
                literary purposes
              </li>
              <li>
                If the information is publicly available and is specified by the
                regulations
              </li>
            </ul>
          </div>
        )}

        {renderSection(
          "whoshare",
          "4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short:</em>
              </strong>
              <em>
                {" "}
                We may share information in specific situations described in
                this section and/or with the following third parties.
              </em>
            </p>
            <p className="mb-4">
              We may need to share your personal information in the following
              situations:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>
                <strong>Business Transfers.</strong> We may share or transfer
                your information in connection with, or during negotiations of,
                any merger, sale of company assets, financing, or acquisition of
                all or a portion of our business to another company.
              </li>
              <li>
                <strong>When we use Google Maps Platform APIs.</strong> We may
                share your information with certain Google Maps Platform APIs
                (e.g., Google Maps API, Places API). Google Maps uses GPS,
                Wi-Fi, and cell towers to estimate your location. GPS is
                accurate to about 20 meters, while Wi-Fi and cell towers help
                improve accuracy when GPS signals are weak, like indoors. This
                data helps Google Maps provide directions, but it is not always
                perfectly precise. We obtain and store on your device ("cache")
                your location. You may revoke your consent anytime by contacting
                us at the contact details provided at the end of this document.
              </li>
              <li>
                <strong>Business Partners.</strong> We may share your
                information with our business partners to offer you certain
                products, services, or promotions.
              </li>
              <li>
                <strong>Offer Wall.</strong> Our application(s) may display a
                third-party hosted "offer wall." Such an offer wall allows
                third-party advertisers to offer virtual currency, gifts, or
                other items to users in return for the acceptance and completion
                of an advertisement offer. Such an offer wall may appear in our
                application(s) and be displayed to you based on certain data,
                such as your geographic area or demographic information. When
                you click on an offer wall, you will be brought to an external
                website belonging to other persons and will leave our
                application(s). A unique identifier, such as your user ID, will
                be shared with the offer wall provider in order to prevent fraud
                and properly credit your account with the relevant reward.
              </li>
            </ul>
          </div>
        )}

        {renderSection(
          "3pwebsites",
          "5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short:</em>
              </strong>
              <em>
                {" "}
                We are not responsible for the safety of any information that
                you share with third parties that we may link to or who
                advertise on our Services, but are not affiliated with, our
                Services.
              </em>
            </p>
            <p className="mb-4">
              The Services, including our offer wall, may link to third-party
              websites, online services, or mobile applications and/or contain
              advertisements from third parties that are not affiliated with us
              and which may link to other websites, services, or applications.
              Accordingly, we do not make any guarantee regarding any such third
              parties, and we will not be liable for any loss or damage caused
              by the use of such third-party websites, services, or
              applications. The inclusion of a link towards a third-party
              website, service, or application does not imply an endorsement by
              us. We cannot guarantee the safety and privacy of data you provide
              to any third parties. Any data collected by third parties is not
              covered by this Privacy Notice. We are not responsible for the
              content or privacy and security practices and policies of any
              third parties, including other websites, services, or applications
              that may be linked to or from the Services. You should review the
              policies of such third parties and contact them directly to
              respond to your questions.
            </p>
          </div>
        )}

        {renderSection(
          "cookies",
          "6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short:</em>
              </strong>
              <em>
                {" "}
                We may use cookies and other tracking technologies to collect
                and store your information.
              </em>
            </p>
            <p className="mb-4">
              We may use cookies and similar tracking technologies (like web
              beacons and pixels) to gather information when you interact with
              our Services. Some online tracking technologies help us maintain
              the security of our Services and your account, prevent crashes,
              fix bugs, save your preferences, and assist with basic site
              functions.
            </p>
            <p className="mb-4">
              We also permit third parties and service providers to use online
              tracking technologies on our Services for analytics and
              advertising, including to help manage and display advertisements,
              to tailor advertisements to your interests, or to send abandoned
              shopping cart reminders (depending on your communication
              preferences). The third parties and service providers use their
              technology to provide advertising about products and services
              tailored to your interests which may appear either on our Services
              or on other websites.
            </p>
            <p className="mb-4">
              To the extent these online tracking technologies are deemed to be
              a "sale"/"sharing" (which includes targeted advertising, as
              defined under the applicable laws) under applicable US state laws,
              you can opt out of these online tracking technologies by
              submitting a request as described below under section "
              <a href="#uslaws" className="text-blue-600 hover:underline">
                DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              </a>
              "
            </p>
            <p className="mb-4">
              Specific information about how we use such technologies and how
              you can refuse certain cookies is set out in our Cookie Notice.
            </p>
          </div>
        )}

        {renderSection(
          "inforetain",
          "7. HOW LONG DO WE KEEP YOUR INFORMATION?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this Privacy Notice unless otherwise
                required by law.
              </em>
            </p>
            <p className="mb-4">
              We will only keep your personal information for as long as it is
              necessary for the purposes set out in this Privacy Notice, unless
              a longer retention period is required or permitted by law (such as
              tax, accounting, or other legal requirements). No purpose in this
              notice will require us keeping your personal information for
              longer than the period of time in which users have an account with
              us.
            </p>
            <p className="mb-4">
              When we have no ongoing legitimate business need to process your
              personal information, we will either delete or anonymize such
              information, or, if this is not possible (for example, because
              your personal information has been stored in backup archives),
              then we will securely store your personal information and isolate
              it from any further processing until deletion is possible.
            </p>
          </div>
        )}

        {renderSection(
          "infosafe",
          "8. HOW DO WE KEEP YOUR INFORMATION SAFE?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                We aim to protect your personal information through a system of
                organizational and technical security measures.
              </em>
            </p>
            <p className="mb-4">
              We have implemented appropriate and reasonable technical and
              organizational security measures designed to protect the security
              of any personal information we process. However, despite our
              safeguards and efforts to secure your information, no electronic
              transmission over the Internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
              Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is
              at your own risk. You should only access the Services within a
              secure environment.
            </p>
          </div>
        )}

        {renderSection(
          "infominors",
          "9. DO WE COLLECT INFORMATION FROM MINORS?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short:</em>
              </strong>
              <em>
                {" "}
                We do not knowingly collect data from or market to children
                under 18 years of age.
              </em>
            </p>
            <p className="mb-4">
              We do not knowingly collect, solicit data from, or market to
              children under 18 years of age, nor do we knowingly sell such
              personal information. By using the Services, you represent that
              you are at least 18 or that you are the parent or guardian of such
              a minor and consent to such minor dependent's use of the Services.
              If we learn that personal information from users less than 18
              years of age has been collected, we will deactivate the account
              and take reasonable measures to promptly delete such data from our
              records. If you become aware of any data we may have collected
              from children under age 18, please contact us at{" "}
              <a
                href="mailto:gallyo@opsaway.ai"
                className="text-blue-600 hover:underline"
              >
                gallyo@opsaway.ai
              </a>
              .
            </p>
          </div>
        )}

        {renderSection(
          "privacyrights",
          "10. WHAT ARE YOUR PRIVACY RIGHTS?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short:</em>
              </strong>
              <em>
                {" "}
                Depending on your state of residence in the US or in some
                regions, such as Canada, you have rights that allow you greater
                access to and control over your personal information. You may
                review, change, or terminate your account at any time, depending
                on your country, province, or state of residence.
              </em>
            </p>
            <p className="mb-4">
              In some regions (like Canada), you have certain rights under
              applicable data protection laws. These may include the right (i)
              to request access and obtain a copy of your personal information,
              (ii) to request rectification or erasure; (iii) to restrict the
              processing of your personal information; (iv) if applicable, to
              data portability; and (v) not to be subject to automated
              decision-making. In certain circumstances, you may also have the
              right to object to the processing of your personal information.
              You can make such a request by contacting us by using the contact
              details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS
              NOTICE?" below.
            </p>

            <p className="mb-4">
              We will consider and act upon any request in accordance with
              applicable data protection laws.
            </p>

            <div id="withdrawconsent">
              <p className="mb-4">
                <strong>
                  <u>Withdrawing your consent:</u>
                </strong>{" "}
                If we are relying on your consent to process your personal
                information, which may be express and/or implied consent
                depending on the applicable law, you have the right to withdraw
                your consent at any time. You can withdraw your consent at any
                time by contacting us by using the contact details provided in
                the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.
              </p>
              <p className="mb-4">
                However, please note that this will not affect the lawfulness of
                the processing before its withdrawal nor, when applicable law
                allows, will it affect the processing of your personal
                information conducted in reliance on lawful processing grounds
                other than consent.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-2">Account Information</h3>
              <p className="mb-4">
                If you would at any time like to review or change the
                information in your account or terminate your account, you can:
              </p>
              <ul className="list-disc pl-8 mb-4 text-gray-600">
                <li>Contact us using the contact information provided.</li>
              </ul>
              <p className="mb-4">
                Upon your request to terminate your account, we will deactivate
                or delete your account and information from our active
                databases. However, we may retain some information in our files
                to prevent fraud, troubleshoot problems, assist with any
                investigations, enforce our legal terms and/or comply with
                applicable legal requirements.
              </p>
            </div>

            <p className="mb-4">
              <strong>
                <u>Cookies and similar technologies:</u>
              </strong>{" "}
              Most Web browsers are set to accept cookies by default. If you
              prefer, you can usually choose to set your browser to remove
              cookies and to reject cookies. If you choose to remove cookies or
              reject cookies, this could affect certain features or services of
              our Services. You may also{" "}
              <a
                href="http://www.aboutads.info/choices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                opt out of interest-based advertising by advertisers
              </a>{" "}
              on our Services.
            </p>

            <p className="mb-4">
              If you have questions or comments about your privacy rights, you
              may email us at{" "}
              <a
                href="mailto:connect@leantechops.co"
                className="text-blue-600 hover:underline"
              >
                connect@leantechops.co
              </a>
              .
            </p>
          </div>
        )}

        {renderSection(
          "DNT",
          "11. CONTROLS FOR DO-NOT-TRACK FEATURES",
          <div>
            <p className="mb-4">
              Most web browsers and some mobile operating systems and mobile
              applications include a Do-Not-Track ("DNT") feature or setting you
              can activate to signal your privacy preference not to have data
              about your online browsing activities monitored and collected. At
              this stage, no uniform technology standard for recognizing and
              implementing DNT signals has been finalized. As such, we do not
              currently respond to DNT browser signals or any other mechanism
              that automatically communicates your choice not to be tracked
              online. If a standard for online tracking is adopted that we must
              follow in the future, we will inform you about that practice in a
              revised version of this Privacy Notice.
            </p>
            <p className="mb-4">
              California law requires us to let you know how we respond to web
              browser DNT signals. Because there currently is not an industry or
              legal standard for recognizing or honoring DNT signals, we do not
              respond to them at this time.
            </p>
          </div>
        )}

        {renderSection(
          "uslaws",
          "12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                If you are a resident of California, Colorado, Connecticut,
                Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota,
                Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode
                Island, Tennessee, Texas, Utah, or Virginia, you may have the
                right to request access to and receive details about the
                personal information we maintain about you and how we have
                processed it, correct inaccuracies, get a copy of, or delete
                your personal information. You may also have the right to
                withdraw your consent to our processing of your personal
                information. These rights may be limited in some circumstances
                by applicable law. More information is provided below.
              </em>
            </p>

            <h3 className="text-lg font-bold mb-2">
              Categories of Personal Information We Collect
            </h3>
            <p className="mb-4">
              We have collected the following categories of personal information
              in the past twelve (12) months:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Category
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Examples
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Collected
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      A. Identifiers
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Contact details, such as real name, alias, postal address,
                      telephone or mobile contact number, unique personal
                      identifier, online identifier, Internet Protocol address,
                      email address, and account name
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      B. Personal information as defined in the California
                      Customer Records statute
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Name, contact information, education, employment,
                      employment history, and financial information
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      C. Protected classification characteristics under state or
                      federal law
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Gender, age, date of birth, race and ethnicity, national
                      origin, marital status, and other demographic data
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      D. Commercial information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Transaction information, purchase history, financial
                      details, and payment information
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      E. Biometric information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Fingerprints and voiceprints
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      F. Internet or other similar network activity
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Browsing history, search history, online behavior,
                      interest data, and interactions with our and other
                      websites, applications, systems, and advertisements
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      G. Geolocation data
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Device location
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      H. Audio, electronic, sensory, or similar information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Images and audio, video or call recordings created in
                      connection with our business activities
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      I. Professional or employment-related information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Business contact details in order to provide you our
                      Services at a business level or job title, work history,
                      and professional qualifications if you apply for a job
                      with us
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      J. Education Information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Student records and directory information
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      K. Inferences drawn from collected personal information
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Inferences drawn from any of the collected personal
                      information listed above to create a profile or summary
                      about, for example, an individual's preferences and
                      characteristics
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      L. Sensitive personal Information
                    </td>
                    <td className="border border-gray-300 px-4 py-2"></td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      NO
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mb-4">
              We may also collect other personal information outside of these
              categories through instances where you interact with us in person,
              online, or by phone or mail in the context of:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>Receiving help through our customer support channels;</li>
              <li>Participation in customer surveys or contests; and</li>
              <li>
                Facilitation in the delivery of our Services and to respond to
                your inquiries.
              </li>
            </ul>

            <p className="mb-4">
              We will use and retain the collected personal information as
              needed to provide the Services or for:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>Category H - As long as the user has an account with us</li>
            </ul>

            <h3 className="text-lg font-bold mb-2">
              Sources of Personal Information
            </h3>
            <p className="mb-4">
              Learn more about the sources of personal information we collect in
              "
              <a href="#infocollect" className="text-blue-600 hover:underline">
                WHAT INFORMATION DO WE COLLECT?
              </a>
              "
            </p>

            <h3 className="text-lg font-bold mb-2">
              How We Use and Share Personal Information
            </h3>
            <p className="mb-4">
              Learn more about how we use your personal information in the
              section, "
              <a href="#infouse" className="text-blue-600 hover:underline">
                HOW DO WE PROCESS YOUR INFORMATION?
              </a>
              "
            </p>

            <p className="mb-4">
              <strong>Will your information be shared with anyone else?</strong>
            </p>
            <p className="mb-4">
              We may disclose your personal information with our service
              providers pursuant to a written contract between us and each
              service provider. Learn more about how we disclose personal
              information to in the section, "
              <a href="#whoshare" className="text-blue-600 hover:underline">
                WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
              </a>
              "
            </p>
            <p className="mb-4">
              We may use your personal information for our own business
              purposes, such as for undertaking internal research for
              technological development and demonstration. This is not
              considered to be "selling" of your personal information.
            </p>
            <p className="mb-4">
              We have not disclosed, sold, or shared any personal information to
              third parties for a business or commercial purpose in the
              preceding twelve (12) months. We will not sell or share personal
              information in the future belonging to website visitors, users,
              and other consumers.
            </p>

            <h3 className="text-lg font-bold mb-2">Your Rights</h3>
            <p className="mb-4">
              You have rights under certain US state data protection laws.
              However, these rights are not absolute, and in certain cases, we
              may decline your request as permitted by law. These rights
              include:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>
                <strong>Right to know</strong> whether or not we are processing
                your personal data
              </li>
              <li>
                <strong>Right to access </strong>your personal data
              </li>
              <li>
                <strong>Right to correct </strong>inaccuracies in your personal
                data
              </li>
              <li>
                <strong>Right to request</strong> the deletion of your personal
                data
              </li>
              <li>
                <strong>Right to obtain a copy </strong>of the personal data you
                previously shared with us
              </li>
              <li>
                <strong>Right to non-discrimination</strong> for exercising your
                rights
              </li>
              <li>
                <strong>Right to opt out</strong> of the processing of your
                personal data if it is used for targeted advertising (or sharing
                as defined under California's privacy law), the sale of personal
                data, or profiling in furtherance of decisions that produce
                legal or similarly significant effects ("profiling")
              </li>
            </ul>

            <p className="mb-4">
              Depending upon the state where you live, you may also have the
              following rights:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>
                Right to access the categories of personal data being processed
                (as permitted by applicable law, including the privacy law in
                Minnesota)
              </li>
              <li>
                Right to obtain a list of the categories of third parties to
                which we have disclosed personal data (as permitted by
                applicable law, including the privacy law in California,
                Delaware, and Maryland)
              </li>
              <li>
                Right to obtain a list of specific third parties to which we
                have disclosed personal data (as permitted by applicable law,
                including the privacy law in Minnesota and Oregon)
              </li>
              <li>
                Right to review, understand, question, and correct how personal
                data has been profiled (as permitted by applicable law,
                including the privacy law in Minnesota)
              </li>
              <li>
                Right to limit use and disclosure of sensitive personal data (as
                permitted by applicable law, including the privacy law in
                California)
              </li>
              <li>
                Right to opt out of the collection of sensitive data and
                personal data collected through the operation of a voice or
                facial recognition feature (as permitted by applicable law,
                including the privacy law in Florida)
              </li>
            </ul>

            <h3 className="text-lg font-bold mb-2">
              How to Exercise Your Rights
            </h3>
            <p className="mb-4">
              To exercise these rights, you can contact us by submitting a{" "}
              <a
                href="https://app.termly.io/notify/9c7169fd-ef07-499f-8c42-95f7db9b9faf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                data subject access request
              </a>
              , by emailing us at{" "}
              <a
                href="mailto:gallyo@opsaway.ai"
                className="text-blue-600 hover:underline"
              >
                gallyo@opsaway.ai
              </a>
              , or by referring to the contact details at the bottom of this
              document.
            </p>

            <h3 className="text-lg font-bold mb-2">Request Verification</h3>
            <p className="mb-4">
              Upon receiving your request, we will need to verify your identity
              to determine you are the same person about whom we have the
              information in our system. We will only use personal information
              provided in your request to verify your identity or authority to
              make the request. However, if we cannot verify your identity from
              the information already maintained by us, we may request that you
              provide additional information for the purposes of verifying your
              identity and for security or fraud-prevention purposes.
            </p>
            <p className="mb-4">
              If you submit the request through an authorized agent, we may need
              to collect additional information to verify your identity before
              processing your request and the agent will need to provide a
              written and signed permission from you to submit such request on
              your behalf.
            </p>

            <h3 className="text-lg font-bold mb-2">Appeals</h3>
            <p className="mb-4">
              Under certain US state data protection laws, if we decline to take
              action regarding your request, you may appeal our decision by
              emailing us at{" "}
              <a
                href="mailto:connect@leantechops.co"
                className="text-blue-600 hover:underline"
              >
                connect@leantechops.co
              </a>
              . We will inform you in writing of any action taken or not taken
              in response to the appeal, including a written explanation of the
              reasons for the decisions. If your appeal is denied, you may
              submit a complaint to your state attorney general.
            </p>

            <h3 className="text-lg font-bold mb-2">
              California "Shine The Light" Law
            </h3>
            <p className="mb-4">
              California Civil Code Section 1798.83, also known as the "Shine
              The Light" law, permits our users who are California residents to
              request and obtain from us, once a year and free of charge,
              information about categories of personal information (if any) we
              disclosed to third parties for direct marketing purposes and the
              names and addresses of all third parties with which we shared
              personal information in the immediately preceding calendar year.
              If you are a California resident and would like to make such a
              request, please submit your request in writing to us by using the
              contact details provided in the section "
              <a href="#contact" className="text-blue-600 hover:underline">
                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              </a>
              "
            </p>
          </div>
        )}

        {renderSection(
          "policyupdates",
          "13. DO WE MAKE UPDATES TO THIS NOTICE?",
          <div>
            <p className="mb-4">
              <strong>
                <em>In Short: </em>
              </strong>
              <em>
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws.
              </em>
            </p>
            <p className="mb-4">
              We may update this Privacy Notice from time to time. The updated
              version will be indicated by an updated "Revised" date at the top
              of this Privacy Notice. If we make material changes to this
              Privacy Notice, we may notify you either by prominently posting a
              notice of such changes or by directly sending you a notification.
              We encourage you to review this Privacy Notice frequently to be
              informed of how we are protecting your information.
            </p>
          </div>
        )}

        {renderSection(
          "contact",
          "14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?",
          <div>
            <p className="mb-4">
              If you have questions or comments about this notice, you may email
              us at{" "}
              <a
                href="mailto:gallyo@opsaway.ai"
                className="text-blue-600 hover:underline"
              >
                gallyo@opsaway.ai
              </a>{" "}
              or contact us by post at:
            </p>
            <address className="mb-4 not-italic">
              Lean Tech Ops LLC
              <br />
              608 SPANISH MUSTANG DR
              <br />
              CEDAR PARK, TX 78613
              <br />
              United States
            </address>
          </div>
        )}

        {renderSection(
          "request",
          "15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?",
          <div>
            <p className="mb-4">
              Based on the applicable laws of your country or state of residence
              in the US, you may have the right to request access to the
              personal information we collect from you, details about how we
              have processed it, correct inaccuracies, or delete your personal
              information. You may also have the right to withdraw your consent
              to our processing of your personal information. These rights may
              be limited in some circumstances by applicable law. To request to
              review, update, or delete your personal information, please fill
              out and submit a{" "}
              <a
                href="https://app.termly.io/notify/9c7169fd-ef07-499f-8c42-95f7db9b9faf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                data subject access request
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PrivacyPolicy;
